/**
 * Copyright 2026 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * PDF Diff Report Export Utilities
 * 
 * Provides functions to generate PDF diff reports from commit changes
 * to user stories and issues. Uses jspdf for PDF generation.
 */

import { jsPDF } from 'jspdf';

export interface DiffReportCommit {
  hash: string;
  shortHash: string;
  message: string;
  author: {
    name: string;
    email: string;
  };
  date: string;
}

export interface DiffReportFile {
  path: string;
  status: 'added' | 'modified' | 'deleted' | 'renamed' | 'unknown';
  diff: string;
}

export interface DiffReportOptions {
  title?: string;
  productName?: string;
  pageSize?: 'a4' | 'letter';
  orientation?: 'portrait' | 'landscape';
  includeRawDiff?: boolean;
  includeFileSummary?: boolean;
}

const DEFAULT_OPTIONS: Required<DiffReportOptions> = {
  title: 'Diff Report',
  productName: '',
  pageSize: 'a4',
  orientation: 'portrait',
  includeRawDiff: true,
  includeFileSummary: true,
};

interface PdfColors {
  primary: [number, number, number];
  secondary: [number, number, number];
  accent: [number, number, number];
  text: [number, number, number];
  lightGray: [number, number, number];
  success: [number, number, number];
  warning: [number, number, number];
  error: [number, number, number];
  addedBg: [number, number, number];
  deletedBg: [number, number, number];
}

const COLORS: PdfColors = {
  primary: [0, 102, 204],
  secondary: [100, 100, 100],
  accent: [124, 58, 237],
  text: [33, 33, 33],
  lightGray: [245, 245, 245],
  success: [34, 197, 94],
  warning: [234, 179, 8],
  error: [239, 68, 68],
  addedBg: [220, 252, 231],
  deletedBg: [254, 226, 226],
};

function formatDate(dateString?: string): string {
  if (!dateString) {
    return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getFileStatusColor(status: string): [number, number, number] {
  switch (status) {
    case 'added':
      return COLORS.success;
    case 'deleted':
      return COLORS.error;
    case 'modified':
      return COLORS.warning;
    case 'renamed':
      return COLORS.primary;
    default:
      return COLORS.secondary;
  }
}

function parseDiffContent(diff: string): { added: string[]; removed: string[]; context: string[] } {
  const lines = diff.split('\n');
  const added: string[] = [];
  const removed: string[] = [];
  const context: string[] = [];

  for (const line of lines) {
    if (line.startsWith('+++') || line.startsWith('---') || line.startsWith('@@')) {
      continue;
    }
    if (line.startsWith('+')) {
      added.push(line.substring(1));
    } else if (line.startsWith('-')) {
      removed.push(line.substring(1));
    } else if (line.trim()) {
      context.push(line);
    }
  }

  return { added, removed, context };
}

function extractUserStoryChanges(diff: string, filePath: string): {
  storiesAdded: string[];
  storiesModified: string[];
  storiesRemoved: string[];
  epicsAdded: string[];
  epicsModified: string[];
} {
  const { added, removed } = parseDiffContent(diff);
  
  const storiesAdded: string[] = [];
  const storiesModified: string[] = [];
  const storiesRemoved: string[] = [];
  const epicsAdded: string[] = [];
  const epicsModified: string[] = [];

  const storyIdRegex = /\*\*([A-Z]{3}-[A-Z]{3}-\d{3}):/;
  const epicIdRegex = /^##\s+([A-Z]{3}-[A-Z]{3}):/;

  const addedStoryIds = new Set<string>();
  const removedStoryIds = new Set<string>();
  const addedEpicIds = new Set<string>();
  const removedEpicIds = new Set<string>();

  for (const line of added) {
    const storyMatch = line.match(storyIdRegex);
    if (storyMatch) {
      addedStoryIds.add(storyMatch[1]);
    }
    const epicMatch = line.match(epicIdRegex);
    if (epicMatch) {
      addedEpicIds.add(epicMatch[1]);
    }
  }

  for (const line of removed) {
    const storyMatch = line.match(storyIdRegex);
    if (storyMatch) {
      removedStoryIds.add(storyMatch[1]);
    }
    const epicMatch = line.match(epicIdRegex);
    if (epicMatch) {
      removedEpicIds.add(epicMatch[1]);
    }
  }

  for (const id of addedStoryIds) {
    if (removedStoryIds.has(id)) {
      storiesModified.push(id);
      removedStoryIds.delete(id);
    } else {
      storiesAdded.push(id);
    }
  }
  for (const id of removedStoryIds) {
    storiesRemoved.push(id);
  }

  for (const id of addedEpicIds) {
    if (removedEpicIds.has(id)) {
      epicsModified.push(id);
    } else {
      epicsAdded.push(id);
    }
  }

  return { storiesAdded, storiesModified, storiesRemoved, epicsAdded, epicsModified };
}

function extractIssueChanges(diff: string, filePath: string): {
  issuesAdded: string[];
  issuesModified: string[];
  issuesRemoved: string[];
} {
  const { added, removed } = parseDiffContent(diff);
  
  const issuesAdded: string[] = [];
  const issuesModified: string[] = [];
  const issuesRemoved: string[] = [];

  const issueIdRegex = /^##\s+([A-Z]+-\d+):/m;

  const addedIssueIds = new Set<string>();
  const removedIssueIds = new Set<string>();

  for (const line of added) {
    const match = line.match(issueIdRegex);
    if (match) {
      addedIssueIds.add(match[1]);
    }
  }

  for (const line of removed) {
    const match = line.match(issueIdRegex);
    if (match) {
      removedIssueIds.add(match[1]);
    }
  }

  for (const id of addedIssueIds) {
    if (removedIssueIds.has(id)) {
      issuesModified.push(id);
      removedIssueIds.delete(id);
    } else {
      issuesAdded.push(id);
    }
  }
  for (const id of removedIssueIds) {
    issuesRemoved.push(id);
  }

  return { issuesAdded, issuesModified, issuesRemoved };
}

class DiffReportPdfGenerator {
  private doc: jsPDF;
  private currentY: number = 20;
  private pageWidth: number;
  private pageHeight: number;
  private marginLeft: number = 20;
  private marginRight: number = 20;
  private marginTop: number = 20;
  private marginBottom: number = 25;
  private contentWidth: number;
  private pageNumber: number = 1;
  private options: Required<DiffReportOptions>;

  constructor(options: DiffReportOptions = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
    this.doc = new jsPDF({
      orientation: this.options.orientation,
      unit: 'mm',
      format: this.options.pageSize,
    });
    
    this.pageWidth = this.doc.internal.pageSize.getWidth();
    this.pageHeight = this.doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - this.marginLeft - this.marginRight;
  }

  private checkPageBreak(requiredSpace: number = 30): void {
    if (this.currentY + requiredSpace > this.pageHeight - this.marginBottom) {
      this.addPage();
    }
  }

  private addPage(): void {
    this.doc.addPage();
    this.pageNumber++;
    this.currentY = this.marginTop;
    this.addFooter();
  }

  private addFooter(): void {
    const footerY = this.pageHeight - 10;
    this.doc.setFontSize(9);
    this.doc.setTextColor(...COLORS.secondary);
    
    this.doc.text(
      `Page ${this.pageNumber}`,
      this.pageWidth - this.marginRight,
      footerY,
      { align: 'right' }
    );
    
    this.doc.text(
      `Generated: ${formatDate()}`,
      this.marginLeft,
      footerY,
      { align: 'left' }
    );
  }

  private addTitle(commit: DiffReportCommit): void {
    this.doc.setFillColor(...COLORS.primary);
    this.doc.rect(0, 0, this.pageWidth, 50, 'F');
    
    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(24);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(this.options.title, this.marginLeft, 22);
    
    if (this.options.productName) {
      this.doc.setFontSize(12);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(this.options.productName, this.marginLeft, 32);
    }
    
    this.doc.setFontSize(10);
    this.doc.text(
      `Commit: ${commit.shortHash}`,
      this.pageWidth - this.marginRight,
      22,
      { align: 'right' }
    );
    this.doc.text(
      formatDate(commit.date),
      this.pageWidth - this.marginRight,
      32,
      { align: 'right' }
    );
    
    this.currentY = 60;
    this.addFooter();
  }

  private addCommitInfo(commit: DiffReportCommit): void {
    this.checkPageBreak(50);
    
    this.doc.setFillColor(...COLORS.lightGray);
    this.doc.roundedRect(this.marginLeft, this.currentY - 5, this.contentWidth, 35, 3, 3, 'F');
    
    this.doc.setTextColor(...COLORS.text);
    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Commit Details', this.marginLeft + 10, this.currentY + 5);
    
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    
    const messageLines = this.doc.splitTextToSize(commit.message, this.contentWidth - 20);
    this.doc.text(messageLines, this.marginLeft + 10, this.currentY + 13);
    
    this.doc.setTextColor(...COLORS.secondary);
    this.doc.setFontSize(9);
    this.doc.text(
      `Author: ${commit.author.name} <${commit.author.email}>`,
      this.marginLeft + 10,
      this.currentY + 25
    );
    
    this.currentY += 45;
  }

  private addFileSummary(files: DiffReportFile[]): void {
    this.checkPageBreak(30);
    
    this.doc.setTextColor(...COLORS.text);
    this.doc.setFontSize(14);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Files Changed', this.marginLeft, this.currentY);
    this.currentY += 8;
    
    const productDataFiles = files.filter(f => 
      f.path.includes('data/product/') || 
      f.path.includes('user-stories') ||
      f.path.includes('issues.md')
    );
    
    const otherFiles = files.filter(f => 
      !f.path.includes('data/product/') && 
      !f.path.includes('user-stories') &&
      !f.path.includes('issues.md')
    );
    
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(
      `${productDataFiles.length} product data file(s), ${otherFiles.length} other file(s)`,
      this.marginLeft,
      this.currentY
    );
    this.currentY += 8;
    
    for (const file of files) {
      this.checkPageBreak(10);
      
      const statusColor = getFileStatusColor(file.status);
      
      this.doc.setFillColor(...statusColor);
      this.doc.roundedRect(this.marginLeft, this.currentY - 3, 18, 6, 1, 1, 'F');
      this.doc.setTextColor(255, 255, 255);
      this.doc.setFontSize(7);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text(file.status.toUpperCase(), this.marginLeft + 9, this.currentY, { align: 'center' });
      
      this.doc.setTextColor(...COLORS.text);
      this.doc.setFontSize(9);
      this.doc.setFont('helvetica', 'normal');
      const pathLines = this.doc.splitTextToSize(file.path, this.contentWidth - 25);
      this.doc.text(pathLines[0], this.marginLeft + 22, this.currentY);
      
      this.currentY += 7;
    }
    
    this.currentY += 10;
  }

  private addChangesSummary(
    userStoryChanges: ReturnType<typeof extractUserStoryChanges>,
    issueChanges: ReturnType<typeof extractIssueChanges>
  ): void {
    this.checkPageBreak(60);
    
    this.doc.setTextColor(...COLORS.text);
    this.doc.setFontSize(14);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Changes Summary', this.marginLeft, this.currentY);
    this.currentY += 10;
    
    const totalStoryChanges = 
      userStoryChanges.storiesAdded.length +
      userStoryChanges.storiesModified.length +
      userStoryChanges.storiesRemoved.length;
    
    const totalIssueChanges =
      issueChanges.issuesAdded.length +
      issueChanges.issuesModified.length +
      issueChanges.issuesRemoved.length;
    
    const boxWidth = (this.contentWidth - 10) / 2;
    
    this.doc.setFillColor(...COLORS.accent);
    this.doc.roundedRect(this.marginLeft, this.currentY - 5, boxWidth, 25, 3, 3, 'F');
    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(20);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(String(totalStoryChanges), this.marginLeft + boxWidth / 2, this.currentY + 5, { align: 'center' });
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text('User Story Changes', this.marginLeft + boxWidth / 2, this.currentY + 13, { align: 'center' });
    
    this.doc.setFillColor(...COLORS.primary);
    this.doc.roundedRect(this.marginLeft + boxWidth + 10, this.currentY - 5, boxWidth, 25, 3, 3, 'F');
    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(20);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(String(totalIssueChanges), this.marginLeft + boxWidth + 10 + boxWidth / 2, this.currentY + 5, { align: 'center' });
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text('Issue Changes', this.marginLeft + boxWidth + 10 + boxWidth / 2, this.currentY + 13, { align: 'center' });
    
    this.currentY += 35;
    
    if (totalStoryChanges > 0) {
      this.addChangesSection('User Stories', userStoryChanges.storiesAdded, userStoryChanges.storiesModified, userStoryChanges.storiesRemoved);
    }
    
    if (userStoryChanges.epicsAdded.length > 0 || userStoryChanges.epicsModified.length > 0) {
      this.addChangesSection('Epics', userStoryChanges.epicsAdded, userStoryChanges.epicsModified, []);
    }
    
    if (totalIssueChanges > 0) {
      this.addChangesSection('Issues', issueChanges.issuesAdded, issueChanges.issuesModified, issueChanges.issuesRemoved);
    }
  }

  private addChangesSection(title: string, added: string[], modified: string[], removed: string[]): void {
    this.checkPageBreak(30);
    
    this.doc.setTextColor(...COLORS.text);
    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(title, this.marginLeft, this.currentY);
    this.currentY += 8;
    
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'normal');
    
    if (added.length > 0) {
      this.doc.setTextColor(...COLORS.success);
      this.doc.text(`Added (${added.length}): ${added.join(', ')}`, this.marginLeft + 5, this.currentY);
      this.currentY += 6;
    }
    
    if (modified.length > 0) {
      this.doc.setTextColor(...COLORS.warning);
      this.doc.text(`Modified (${modified.length}): ${modified.join(', ')}`, this.marginLeft + 5, this.currentY);
      this.currentY += 6;
    }
    
    if (removed.length > 0) {
      this.doc.setTextColor(...COLORS.error);
      this.doc.text(`Removed (${removed.length}): ${removed.join(', ')}`, this.marginLeft + 5, this.currentY);
      this.currentY += 6;
    }
    
    this.currentY += 5;
  }

  private addDiffSection(files: DiffReportFile[]): void {
    const productDataFiles = files.filter(f => 
      f.path.includes('data/product/') || 
      f.path.includes('user-stories') ||
      f.path.includes('issues.md')
    );
    
    if (productDataFiles.length === 0) return;
    
    this.checkPageBreak(30);
    
    this.doc.setTextColor(...COLORS.text);
    this.doc.setFontSize(14);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Detailed Changes', this.marginLeft, this.currentY);
    this.currentY += 10;
    
    for (const file of productDataFiles) {
      this.addFileDiff(file);
    }
  }

  private addFileDiff(file: DiffReportFile): void {
    this.checkPageBreak(40);
    
    this.doc.setFillColor(...COLORS.lightGray);
    this.doc.roundedRect(this.marginLeft, this.currentY - 5, this.contentWidth, 12, 2, 2, 'F');
    
    const statusColor = getFileStatusColor(file.status);
    this.doc.setFillColor(...statusColor);
    this.doc.roundedRect(this.marginLeft + 3, this.currentY - 2, 18, 6, 1, 1, 'F');
    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(7);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(file.status.toUpperCase(), this.marginLeft + 12, this.currentY + 1, { align: 'center' });
    
    this.doc.setTextColor(...COLORS.text);
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(file.path, this.marginLeft + 25, this.currentY + 2);
    
    this.currentY += 15;
    
    if (this.options.includeRawDiff && file.diff) {
      const { added, removed } = parseDiffContent(file.diff);
      
      this.doc.setFontSize(8);
      this.doc.setFont('courier', 'normal');
      
      // Show all removed lines first
      for (const line of removed) {
        this.checkPageBreak(5);
        
        this.doc.setFillColor(...COLORS.deletedBg);
        this.doc.rect(this.marginLeft, this.currentY - 3, this.contentWidth, 4, 'F');
        this.doc.setTextColor(...COLORS.error);
        // Truncate very long lines to fit on page, but show more content
        const maxLineLength = this.options.orientation === 'landscape' ? 150 : 100;
        const truncatedLine = line.length > maxLineLength ? line.substring(0, maxLineLength) + '...' : line;
        this.doc.text(`- ${truncatedLine}`, this.marginLeft + 2, this.currentY);
        this.currentY += 4;
      }
      
      // Show all added lines
      for (const line of added) {
        this.checkPageBreak(5);
        
        this.doc.setFillColor(...COLORS.addedBg);
        this.doc.rect(this.marginLeft, this.currentY - 3, this.contentWidth, 4, 'F');
        this.doc.setTextColor(...COLORS.success);
        // Truncate very long lines to fit on page, but show more content
        const maxLineLength = this.options.orientation === 'landscape' ? 150 : 100;
        const truncatedLine = line.length > maxLineLength ? line.substring(0, maxLineLength) + '...' : line;
        this.doc.text(`+ ${truncatedLine}`, this.marginLeft + 2, this.currentY);
        this.currentY += 4;
      }
    }
    
    this.currentY += 10;
  }

  public generate(commit: DiffReportCommit, files: DiffReportFile[]): jsPDF {
    this.addTitle(commit);
    this.addCommitInfo(commit);
    
    if (this.options.includeFileSummary) {
      this.addFileSummary(files);
    }
    
    const allUserStoryChanges = {
      storiesAdded: [] as string[],
      storiesModified: [] as string[],
      storiesRemoved: [] as string[],
      epicsAdded: [] as string[],
      epicsModified: [] as string[],
    };
    
    const allIssueChanges = {
      issuesAdded: [] as string[],
      issuesModified: [] as string[],
      issuesRemoved: [] as string[],
    };
    
    for (const file of files) {
      if (file.path.includes('user-stories') && file.diff) {
        const changes = extractUserStoryChanges(file.diff, file.path);
        allUserStoryChanges.storiesAdded.push(...changes.storiesAdded);
        allUserStoryChanges.storiesModified.push(...changes.storiesModified);
        allUserStoryChanges.storiesRemoved.push(...changes.storiesRemoved);
        allUserStoryChanges.epicsAdded.push(...changes.epicsAdded);
        allUserStoryChanges.epicsModified.push(...changes.epicsModified);
      }
      
      if (file.path.includes('issues.md') && file.diff) {
        const changes = extractIssueChanges(file.diff, file.path);
        allIssueChanges.issuesAdded.push(...changes.issuesAdded);
        allIssueChanges.issuesModified.push(...changes.issuesModified);
        allIssueChanges.issuesRemoved.push(...changes.issuesRemoved);
      }
    }
    
    this.addChangesSummary(allUserStoryChanges, allIssueChanges);
    
    if (this.options.includeRawDiff) {
      this.addDiffSection(files);
    }
    
    return this.doc;
  }

  public save(filename: string): void {
    this.doc.save(filename);
  }
}

export function generateDiffReportFilename(commitHash: string, productName?: string): string {
  const timestamp = new Date().toISOString().split('T')[0];
  const prefix = productName 
    ? `${productName.toLowerCase().replace(/\s+/g, '-')}-diff-report`
    : 'diff-report';
  return `${prefix}-${commitHash.substring(0, 7)}-${timestamp}.pdf`;
}

export function exportDiffReportToPdf(
  commit: DiffReportCommit,
  files: DiffReportFile[],
  options: DiffReportOptions = {}
): { filename: string; userStoryCount: number; issueCount: number } {
  const generator = new DiffReportPdfGenerator(options);
  const pdf = generator.generate(commit, files);
  
  let userStoryCount = 0;
  let issueCount = 0;
  
  for (const file of files) {
    if (file.path.includes('user-stories') && file.diff) {
      const changes = extractUserStoryChanges(file.diff, file.path);
      userStoryCount += 
        changes.storiesAdded.length +
        changes.storiesModified.length +
        changes.storiesRemoved.length;
    }
    
    if (file.path.includes('issues.md') && file.diff) {
      const changes = extractIssueChanges(file.diff, file.path);
      issueCount += 
        changes.issuesAdded.length +
        changes.issuesModified.length +
        changes.issuesRemoved.length;
    }
  }
  
  const filename = generateDiffReportFilename(
    commit.hash,
    options.productName
  );
  
  pdf.save(filename);
  
  return {
    filename,
    userStoryCount,
    issueCount,
  };
}

export { DiffReportPdfGenerator };
